import React, {ReactElement} from 'react';
import {
    Select as BaseSelect,
    SelectOwnerState,
    SelectProps,
    selectClasses,
    SelectRootSlotProps,
} from '@mui/base/Select';
import { flip, offset, shift, size } from '@floating-ui/react-dom';
import {styled} from '@mui/material';
import {SVGS} from '../../../../assets/images';
import Listbox, { Option } from '../../listbox';

const StyledButton = styled('button', { shouldForwardProp: () => true })(
    () => `
  margin: 0;
  width: 100%;
  position: relative;
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0px;
  min-width: 0;
  box-sizing: border-box;
  padding: 11px 8px 11px 16px;
  gap: 8px;
  border-radius: 8px;
  background: var(--background-primary);
  color: var(--font-foreground);
  display: flex;
  align-items: center;
  box-shadow: none !important;
  border-color: var(--form-element-border-color) !important;
  white-space: nowrap;
  justify-content: space-between;

  @media (min-width: 768px) {
    width: auto;
    min-width: 156px;
  }
  
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 120ms;
  
  & > svg {
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 250ms;
    path {
        fill: var(--text-primary);
        fill-opacity: 0.4;
    }
  }

  &.${selectClasses.expanded} {
    color: var(--text-primary);
    border-color: var(--font-foreground) !important;
    
    & > svg {
        transform: rotate(180deg);
        path {
           fill: var(--text-primary);
           fill-opacity: 0.4;
        }
    }
  }

  &.${selectClasses.focusVisible} {
    outline: 0;
    border-color: var(--font-foreground) !important;
  }

  &.dropdown-error,
  &[aria-invalid="true"] {
    border-color: var(--text-accent) !important;
  }
  `,
);

type SelectedTextColorMode = 'default' | 'secondary';

type RootSlotProps<
    TValue extends NonNullable<unknown>,
    Multiple extends boolean,
> = SelectRootSlotProps<TValue, Multiple> & {
    selectedTextColor?: SelectedTextColorMode;
};

const StyledLabel = styled('span', {
    shouldForwardProp: (prop) => prop !== '$hasValue' && prop !== '$forceSecondary'
})<{ $hasValue: boolean; $forceSecondary: boolean }>`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 8px;
  text-align: left;
  color: ${(props) => props.$forceSecondary
        ? 'var(--text-secondary)'
        : props.$hasValue
            ? 'var(--text-primary)'
            : 'var(--text-secondary)'
};
`;

const CustomButton = React.forwardRef(function CustomButton<
    TValue extends NonNullable<unknown>,
    Multiple extends boolean,
>(
    props: RootSlotProps<TValue, Multiple>,
    ref: React.ForwardedRef<HTMLButtonElement>,
) {
    const { ownerState, selectedTextColor = 'default', ...other } = props;
    const currentValue = ownerState.value;
    const hasValue = Array.isArray(currentValue)
        ? currentValue.length > 0
        : currentValue !== null && currentValue !== undefined && currentValue !== '';
    const forceSecondary = selectedTextColor === 'secondary';
    return (
        <StyledButton type="button" {...other} ref={ref}>
            <StyledLabel $hasValue={hasValue} $forceSecondary={forceSecondary}>{other.children}</StyledLabel>
            <SVGS.KeyboardArrowDownIcon />
        </StyledButton>
    );
});

type DropdownProps<
    TValue extends NonNullable<unknown>,
    Multiple extends boolean,
> = SelectProps<TValue, Multiple> & {
    selectedTextColor?: SelectedTextColorMode;
};

const Dropdown = React.forwardRef(function Select<
    TValue extends NonNullable<unknown>,
    Multiple extends boolean,
>(props: DropdownProps<TValue, Multiple>, ref: React.ForwardedRef<HTMLButtonElement>) {
    const {
        slotProps: providedSlotProps,
        slots: providedSlots,
        selectedTextColor = 'default',
        ...restProps
    } = props;

    const slots: SelectProps<TValue, Multiple>['slots'] = {
        root: CustomButton,
        listbox: Listbox,
        ...(providedSlots ?? {}),
    };

    const {
        popup: providedPopupSlotProps,
        listbox: providedListboxSlotProps,
        ...otherSlotProps
    } = providedSlotProps ?? {};

    const resolvePopupSlotProps = (ownerState: SelectOwnerState<TValue, Multiple>) => {
        const resolvedPopupSlotProps =
            typeof providedPopupSlotProps === 'function'
                ? providedPopupSlotProps(ownerState)
                : providedPopupSlotProps ?? {};

        const { style: popupStyle, middleware: popupMiddleware, ...popupRest } = resolvedPopupSlotProps;

        const dropdownOffset = 5;
        const dropdownOffsetTop = 5;
        const resolveOffsetMiddleware = () => offset(({ placement }) => {
            const primaryPlacement = placement.split('-')[0];
            return {
                mainAxis: primaryPlacement === 'top'
                    ? dropdownOffsetTop
                    : dropdownOffset,
            };
        });

        const effectiveMiddleware = popupMiddleware
            ? [...popupMiddleware]
            : [resolveOffsetMiddleware(), flip(), shift()];

        const widthMiddleware = size({
            apply({ rects, elements }) {
                const width = `${rects.reference.width}px`;
                elements.floating.style.minWidth = width;
                elements.floating.style.width = width;
            },
        });

        return {
            ...popupRest,
            style: {
                zIndex: 1300,
                ...popupStyle,
            },
            middleware: [...effectiveMiddleware, widthMiddleware],
        };
    };

    const resolveListboxSlotProps = (ownerState: SelectOwnerState<TValue, Multiple>) => {
        const resolvedListboxSlotProps =
            typeof providedListboxSlotProps === 'function'
                ? providedListboxSlotProps(ownerState)
                : providedListboxSlotProps ?? {};

        const { style: listboxStyle, ...listboxRest } = resolvedListboxSlotProps;

        return {
            ...listboxRest,
            style: {
                maxHeight: '250px',
                overflowY: 'auto',
                ...listboxStyle,
            },
        };
    };

    return <BaseSelect
        {...restProps} 
        ref={ref} 
        slots={slots} 
        slotProps={{
            ...otherSlotProps,
            root: {
                ...(otherSlotProps?.root ?? {}),
                selectedTextColor,
            } as RootSlotProps<TValue, Multiple>,
            popup: resolvePopupSlotProps,
            listbox: resolveListboxSlotProps,
        }} />;
}) as <TValue extends NonNullable<unknown>, Multiple extends boolean>(
    props: DropdownProps<TValue, Multiple> & React.RefAttributes<HTMLButtonElement>,
) => ReactElement;

export { Dropdown }
export { Option }
export type { DropdownProps }
